// frontend/packages/business-console/src/pages/BuildInterview.jsx
//
// Work item 8.7 - the S2 build interview (discovery/12 §2). A
// structured, resumable interview: the server-side FSM decides what
// to ask; this page renders the scripted question for the current
// step, collects the answer (freeform text or structured widgets),
// and shows live progress + the running brief summary.
//
// ⚠ Console builds are NOT runnable in the Claude sandbox, the
// deploy build is the gate and a founder eyeball is owed on this
// page (the 5.10 lesson).
//
// Copy rule: every string here is customer-visible. No em-dashes.

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import GroupsIcon from '@mui/icons-material/Groups';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import AddIcon from '@mui/icons-material/Add';

import useDirectUpload from '../hooks/useDirectUpload';
import ProofInput from './BuildInterviewProofInput';
import InterviewShell from '../components/build-interview/InterviewShell';
import JourneyRail from '../components/build-interview/JourneyRail';
import QuestionCard from '../components/build-interview/QuestionCard';
import FollowUpCard from '../components/build-interview/FollowUpCard';
import BriefPanel from '../components/build-interview/BriefPanel';
import ReviewConfirm from '../components/build-interview/ReviewConfirm';
import BuildingScreen from '../components/build-interview/BuildingScreen';
import BuildInterviewAnimations from '../components/build-interview/BuildInterviewAnimations';
import MobileStepBar from '../components/build-interview/MobileStepBar';
import SitePreview from '../components/build-interview/SitePreview';
import CompletionRing from '../components/build-interview/CompletionRing';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import {
  confirmBuildInterview,
  getBuildInterview,
  resetBuildInterview,
  skipBuildInterviewStep,
  submitBuildInterviewTurn,
} from '../services/buildFunnelService';

const TONES = [
  { value: 'professional', label: 'Professional' },
  { value: 'friendly', label: 'Friendly' },
  { value: 'premium', label: 'Premium' },
  { value: 'straight_talking', label: 'Straight talking' },
];

const CTAS = [
  { value: 'call', label: 'Call us' },
  { value: 'form', label: 'Send the contact form' },
  { value: 'book', label: 'Book an appointment' },
];

const PALETTE_CHOICES = [
  '#1a7f37',
  '#0f3d22',
  '#1565c0',
  '#0d2c54',
  '#b26a00',
  '#7b1fa2',
  '#c62828',
  '#37474f',
  '#00695c',
  '#212121',
];

/** Pick a legible check-mark color (black or white) for a given swatch
 *  background, by relative luminance. Keeps the selected indicator visible
 *  on both light and dark swatches. */
function readableText(hex) {
  const h = String(hex || '').replace('#', '');
  if (h.length !== 6) return '#fff';
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  if ([r, g, b].some((n) => Number.isNaN(n))) return '#fff';
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.6 ? '#000' : '#fff';
}

function errMessage(e) {
  return (
    (e && e.response && e.response.data && e.response.data.error) ||
    'Something went wrong. Try again.'
  );
}

// Upload limits, stated up front and enforced client-side (WS-F A3).
const MAX_UPLOAD_MB = 5;
const MAX_UPLOAD_BYTES = MAX_UPLOAD_MB * 1024 * 1024;
const UPLOAD_ACCEPTED_TYPES = [
  'image/png',
  'image/jpeg',
  'image/svg+xml',
  'image/webp',
];
const UPLOAD_HINT = `Accepts PNG, JPG, SVG, or WebP, up to ${MAX_UPLOAD_MB} MB.`;

/** Structured input widgets for the steps that never touch the AI. */
function BrandInput({ onSubmit, busy, onChange }) {
  const { upload, uploading } = useDirectUpload();
  // logo = { publicId, url } once uploaded; null = use a text wordmark.
  const [logo, setLogo] = useState(null);
  const [logoError, setLogoError] = useState('');
  const [palette, setPalette] = useState([]);
  // Colors the customer added via the picker, beyond the presets.
  const [customColors, setCustomColors] = useState([]);
  const [tone, setTone] = useState('');
  const fileRef = useRef(null);

  // Report the live selection up so the SitePreview recolours as they choose.
  useEffect(() => {
    if (onChange) onChange({ palette, tone, logoUrl: logo ? logo.url : null });
  }, [palette, tone, logo, onChange]);

  // Single primary brand color (the redesigned seedColor model): selecting a
  // swatch sets THE color; the backend derives an AA-safe primary + accent from
  // it via palette.ts. Tap the selected swatch again to clear.
  const togglePalette = (hex) => setPalette((p) => (p[0] === hex ? [] : [hex]));

  // Free-text hex entry, so a customer can paste a brand-guideline color
  // instead of only using the swatches or the native picker (WS-F A2).
  const [hexInput, setHexInput] = useState('');
  const commitHex = () => {
    const v = hexInput.trim().toLowerCase();
    const normalized = v && !v.startsWith('#') ? `#${v}` : v;
    if (/^#[0-9a-f]{6}$/.test(normalized)) {
      addCustomColor(normalized);
      setHexInput('');
    }
  };

  const addCustomColor = (raw) => {
    const hex = String(raw || '').toLowerCase();
    if (!/^#[0-9a-f]{6}$/.test(hex)) return;
    setCustomColors((c) =>
      c.includes(hex) || PALETTE_CHOICES.includes(hex) ? c : [...c, hex]
    );
    setPalette([hex]); // single primary color (seedColor model)
  };

  const handleLogoFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    e.target.value = '';
    setLogoError('');
    // State accepted types + size up front, and on rejection (WS-F A3).
    if (file.type && !UPLOAD_ACCEPTED_TYPES.includes(file.type)) {
      setLogoError(
        'That file type is not supported. Use a PNG, JPG, SVG, or WebP.'
      );
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setLogoError(
        `That logo is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). The limit is ${MAX_UPLOAD_MB} MB.`
      );
      return;
    }
    try {
      const result = await upload(file);
      setLogo({ publicId: result.public_id, url: result.secure_url });
    } catch (err) {
      setLogoError(
        `That logo did not upload. Use a PNG, JPG, SVG, or WebP under ${MAX_UPLOAD_MB} MB.`
      );
    }
  };

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Your logo (optional). You can add or change it later.
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 88,
              height: 88,
              flexShrink: 0,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              overflow: 'hidden',
              display: 'grid',
              placeItems: 'center',
              bgcolor: 'action.hover',
            }}
          >
            {uploading ? (
              <CircularProgress size={24} />
            ) : logo ? (
              <img
                src={logo.url}
                alt="Logo preview"
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            ) : (
              <ImageOutlinedIcon
                sx={{ color: 'action.disabled', fontSize: 32 }}
              />
            )}
          </Box>
          <Stack spacing={1} alignItems="flex-start">
            <Button
              variant="outlined"
              startIcon={
                uploading ? (
                  <CircularProgress size={16} />
                ) : (
                  <FileUploadOutlinedIcon />
                )
              }
              disabled={uploading || busy}
              onClick={() => fileRef.current && fileRef.current.click()}
            >
              {logo ? 'Replace logo' : 'Upload logo'}
            </Button>
            {logo ? (
              <Button
                size="small"
                color="inherit"
                disabled={uploading || busy}
                onClick={() => setLogo(null)}
              >
                Use a text wordmark instead
              </Button>
            ) : (
              <Typography variant="caption" color="text.secondary">
                No logo? We use a clean text wordmark.
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary">
              {UPLOAD_HINT}
            </Typography>
          </Stack>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/svg+xml,image/webp"
            onChange={handleLogoFile}
            style={{ display: 'none' }}
          />
        </Stack>
        {logoError ? (
          <Alert severity="error" sx={{ mt: 1 }}>
            {logoError}
          </Alert>
        ) : null}
      </Box>
      <Box>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Pick your main brand color, or add your own:
        </Typography>
        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
          alignItems="center"
        >
          {[...PALETTE_CHOICES, ...customColors].map((hex) => {
            const selected = palette.includes(hex);
            return (
              <Box
                key={hex}
                role="button"
                aria-label={`Color ${hex}${selected ? ', selected' : ''}`}
                title={hex}
                onClick={() => togglePalette(hex)}
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  bgcolor: hex,
                  cursor: 'pointer',
                  border: '2px solid',
                  borderColor: selected ? 'text.primary' : 'rgba(0,0,0,0.18)',
                  boxShadow: selected ? 2 : 0,
                  display: 'grid',
                  placeItems: 'center',
                  transition: 'transform 0.1s ease',
                  '&:hover': { transform: 'scale(1.08)' },
                }}
              >
                {selected ? (
                  <CheckCircleIcon
                    sx={{ color: readableText(hex), fontSize: 20 }}
                  />
                ) : null}
              </Box>
            );
          })}
          <Box
            component="label"
            title="Add a custom color"
            sx={{
              position: 'relative',
              width: 40,
              height: 40,
              borderRadius: '50%',
              cursor: 'pointer',
              border: '2px dashed',
              borderColor: 'divider',
              color: 'text.secondary',
              display: 'grid',
              placeItems: 'center',
              '&:hover': { borderColor: 'text.primary', color: 'text.primary' },
            }}
          >
            <AddIcon fontSize="small" />
            <input
              type="color"
              aria-label="Add a custom color"
              onChange={(e) => addCustomColor(e.target.value)}
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                opacity: 0,
                cursor: 'pointer',
              }}
            />
          </Box>
          <TextField
            size="small"
            label="Hex"
            placeholder="#2563eb"
            value={hexInput}
            onChange={(e) => setHexInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commitHex();
              }
            }}
            onBlur={commitHex}
            inputProps={{
              maxLength: 7,
              'aria-label': 'Add a brand color by hex code',
            }}
            helperText="Paste a brand color"
            sx={{ width: 140 }}
          />
        </Stack>
        {palette.length > 0 ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1, display: 'block' }}
          >
            Selected: {palette.join(', ')}
          </Typography>
        ) : null}
      </Box>
      <TextField
        select
        label="Tone"
        value={tone}
        onChange={(e) => setTone(e.target.value)}
        sx={{ maxWidth: 320 }}
      >
        {TONES.map((t) => (
          <MenuItem key={t.value} value={t.value}>
            {t.label}
          </MenuItem>
        ))}
      </TextField>
      <Box>
        <Button
          variant="contained"
          disabled={busy || uploading || !tone}
          onClick={() =>
            onSubmit({
              logoAssetId: logo ? logo.publicId : null,
              useTextWordmark: !logo,
              // The redesigned single-seed model: send the picked primary as
              // seedColor so the backend derives an AA-safe palette (palette.ts),
              // instead of the deprecated raw palette[] path. Omitted when no
              // color was picked (backend applies an archetype default).
              ...(palette[0] ? { seedColor: palette[0], variant: 'bold' } : {}),
              tone,
            })
          }
        >
          Save look and feel
        </Button>
      </Box>
    </Stack>
  );
}

function UrlListInput({ label, onSubmit, busy }) {
  const [urls, setUrls] = useState(['', '', '', '', '']);
  const filled = urls.map((u) => u.trim()).filter(Boolean);
  return (
    <Stack spacing={1.5}>
      {urls.map((u, i) => (
        <TextField
          key={i}
          size="small"
          placeholder={`https://example${i + 1}.com`}
          value={u}
          onChange={(e) =>
            setUrls((prev) =>
              prev.map((x, j) => (j === i ? e.target.value : x))
            )
          }
        />
      ))}
      <Box>
        <Button
          variant="contained"
          disabled={busy || filled.length === 0}
          onClick={() => onSubmit(filled)}
        >
          {label}
        </Button>
      </Box>
    </Stack>
  );
}

function GoalsInput({ onSubmit, busy }) {
  const [cta, setCta] = useState('');
  const [intent, setIntent] = useState('');
  return (
    <Stack spacing={2}>
      <TextField
        select
        label="What should visitors do first?"
        value={cta}
        onChange={(e) => setCta(e.target.value)}
        sx={{ maxWidth: 360 }}
      >
        {CTAS.map((c) => (
          <MenuItem key={c.value} value={c.value}>
            {c.label}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        label="In one sentence, what should the site achieve?"
        value={intent}
        onChange={(e) => setIntent(e.target.value)}
        inputProps={{ maxLength: 300 }}
      />
      <Box>
        <Button
          variant="contained"
          disabled={busy || !cta}
          onClick={() =>
            onSubmit({
              primaryCta: cta,
              ...(intent.trim() ? { landingIntent: intent.trim() } : {}),
            })
          }
        >
          Save goals
        </Button>
      </Box>
    </Stack>
  );
}

const STRUCTURED_WIDGETS = {
  brand: (props) => <BrandInput {...props} />,
  competitors: (props) => (
    <UrlListInput label="Save competitor sites" {...props} />
  ),
  favoriteSites: (props) => (
    <UrlListInput label="Save favorite sites" {...props} />
  ),
  proof: (props) => <ProofInput {...props} />,
  goals: (props) => <GoalsInput {...props} />,
};

export default function BuildInterview() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notReady, setNotReady] = useState('');
  const [acks, setAcks] = useState([]);
  // Conversational re-ask: the answer saved, but the backend returned one
  // scripted follow-up because an optional part of the question was skipped
  // (e.g. hours, primary service). Shown once; "add it" resubmits the step
  // with the addition appended to the saved verbatim answer.
  const [followUp, setFollowUp] = useState(null);
  // When set, the customer is re-answering an already-completed step (edit /
  // back nav). The backend overwrites that section; on success we drop the
  // override and return to where they were.
  const [editingStepId, setEditingStepId] = useState(null);
  // Live brand selection from the Look & feel step, for the SitePreview recolour.
  const [brandDraft, setBrandDraft] = useState({
    palette: [],
    tone: '',
    logoUrl: null,
  });
  // Mobile: the brief collapses to a tappable row (open on tap).
  const [briefOpen, setBriefOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const s = await getBuildInterview();
      setState(s);
      setNotReady('');
    } catch (e) {
      const code = e && e.response && e.response.data && e.response.data.code;
      if (code === 'BUILD_JOB_NOT_READY') {
        setNotReady(errMessage(e));
      } else {
        setError(errMessage(e));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const applyResult = (out) => {
    setState(out.state);
    setAcks(out.ack || []);
    setFollowUp(out.followUp || null);
  };

  // One busy/error envelope for every interview action.
  const runAction = async (fn) => {
    setBusy(true);
    setError('');
    try {
      await fn();
    } catch (e) {
      setError(errMessage(e));
    } finally {
      setBusy(false);
    }
  };

  // On a successful turn, clear any edit override so the view returns to the
  // current step / review. A 422 re-ask throws (caught by runAction), so the
  // override is preserved and the customer stays on the step to fix it.
  const runTurn = (payload) =>
    runAction(async () => {
      applyResult(await submitBuildInterviewTurn(payload));
      setEditingStepId(null);
    });

  const runSkip = (stepId) =>
    runAction(async () => {
      applyResult(await skipBuildInterviewStep(stepId));
      setEditingStepId(null);
    });

  // "Add it" on the follow-up: resubmit the SAME step with the addition
  // appended to the saved verbatim answer, so the extractor re-reads the
  // full context and nothing already captured is lost. The backend never
  // re-asks a stamped step, so the next result clears the card either way.
  const runFollowUpAdd = (addition) => {
    if (!followUp) return;
    const prev =
      (state && state.rawAnswers && state.rawAnswers[followUp.stepId]) || '';
    runTurn({
      stepId: followUp.stepId,
      text: prev ? `${prev}\n${addition}` : addition,
    });
  };

  const runConfirm = () =>
    runAction(async () => {
      const out = await confirmBuildInterview();
      setState(out.state);
      setAcks([]);
      setFollowUp(null);
    });

  // Start over: clear the draft, return to step 1. resetBuildInterview returns
  // the fresh interview state (same shape as load()).
  const runReset = () =>
    runAction(async () => {
      const fresh = await resetBuildInterview();
      setState(fresh);
      setAcks([]);
      setFollowUp(null);
      setEditingStepId(null);
    });

  // Navigating anywhere dismisses a pending follow-up (it was an offer).
  const startEdit = (id) => {
    setFollowUp(null);
    setEditingStepId(id);
  };

  const currentStep = useMemo(() => {
    if (!state || !state.progress || !state.progress.nextStepId) return null;
    return state.steps.find((s) => s.id === state.progress.nextStepId) || null;
  }, [state]);

  // The step actually shown: an edit override (re-answering a done step) takes
  // precedence over the server's next step.
  const editingStep = useMemo(
    () =>
      editingStepId && state
        ? state.steps.find((s) => s.id === editingStepId) || null
        : null,
    [editingStepId, state]
  );
  const shownStep = editingStep || currentStep;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (notReady) {
    return (
      <Box sx={{ maxWidth: 720, mx: 'auto', py: 4 }}>
        <Typography variant="h5" gutterBottom>
          Build your website
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          {notReady}
        </Alert>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Your build is still being set up. This usually clears in a moment.
        </Typography>
        <Button variant="contained" onClick={load} disabled={loading}>
          {loading ? 'Checking...' : 'Check again'}
        </Button>
      </Box>
    );
  }

  const progress = (state && state.progress) || { percent: 0 };
  const completed = Boolean(state && state.completed);
  const steps = (state && state.steps) || [];
  const doneCount = steps.filter((s) => s.done).length;
  // The Look & feel step swaps the brief column for a live recolouring preview.
  const isLookAndFeel = Boolean(
    shownStep && shownStep.id === 'brand' && !completed
  );
  const data = (state && state.data) || {};
  const savedBrand = data.brand || {};
  // Prefer the live draft; fall back to any saved brand answer (when re-editing).
  // Saved answers now carry seedColor (the single-color model); still read the
  // legacy palette[] for any in-flight drafts saved before that change.
  const previewColors = brandDraft.palette.length
    ? brandDraft.palette
    : savedBrand.seedColor
      ? [savedBrand.seedColor]
      : savedBrand.palette || [];
  const previewTone = brandDraft.tone || savedBrand.tone || '';

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: '100%',
        width: '100%',
        bgcolor: (theme) =>
          theme.palette.mode === 'dark' ? '#0f172a' : '#e9edf2',
      }}
    >
      <Box
        sx={{
          maxWidth: 1100,
          mx: 'auto',
          py: { xs: 2, md: 4 },
          px: { xs: 2, md: 3 },
        }}
      >
        <BuildInterviewAnimations />
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <InterviewShell
            doneCount={doneCount}
            totalSteps={steps.length || 10}
            percent={progress.percent || 0}
            minutesLeft={Math.max(1, (steps.length || 10) - doneCount)}
          />
        </Box>
        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
          <MobileStepBar
            steps={steps}
            activeId={shownStep ? shownStep.id : null}
            percent={progress.percent || 0}
            doneCount={doneCount}
            totalSteps={steps.length || 10}
            busy={busy}
            completed={completed}
            onSelect={startEdit}
          />
        </Box>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        ) : null}
        {acks.map((a, i) => (
          <Alert key={i} icon={<GroupsIcon />} severity="info" sx={{ mb: 2 }}>
            {a}
          </Alert>
        ))}

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: 2, md: 3 },
            mt: { xs: 2, md: 0 },
          }}
        >
          {/* Left: journey rail (desktop only; hidden on the live-preview step) */}
          <Box
            sx={{
              display: isLookAndFeel ? 'none' : { xs: 'none', md: 'block' },
            }}
          >
            <JourneyRail
              steps={steps}
              activeId={shownStep ? shownStep.id : null}
              busy={busy}
              completed={completed}
              onSelect={startEdit}
            />
          </Box>

          {/* Middle: current step or confirm */}
          <Card
            sx={{
              flex: 1,
              minWidth: { xs: 0, md: 340 },
              borderRadius: '16px',
              boxShadow: (theme) => theme.custom.tokens.cardShadowSoft,
            }}
          >
            <CardContent>
              {completed ? (
                <BuildingScreen teamForkIntro={state.teamForkIntro} />
              ) : followUp ? (
                // The step's answer just saved; offer the one scripted
                // follow-up before moving to the next question.
                <FollowUpCard
                  question={followUp.question}
                  busy={busy}
                  onAdd={runFollowUpAdd}
                  onContinue={() => setFollowUp(null)}
                />
              ) : shownStep ? (
                // key on the step id so switching steps (including into an edit)
                // remounts QuestionCard with fresh answer state instead of leaking
                // the prior step's text. A same-step 422 re-ask keeps the id.
                <Box key={shownStep.id}>
                  <QuestionCard
                    step={shownStep}
                    totalSteps={state.steps.length}
                    editing={Boolean(editingStep)}
                    onCancelEdit={() => setEditingStepId(null)}
                    busy={busy}
                    // Prefill the customer's saved freeform answer when they go
                    // back to edit a completed step (empty on the current step).
                    initialText={
                      editingStep && state.rawAnswers
                        ? state.rawAnswers[shownStep.id] || ''
                        : ''
                    }
                    onSubmitText={(text) =>
                      runTurn({ stepId: shownStep.id, text })
                    }
                    onBack={
                      shownStep.order > 1
                        ? () => {
                            const prev = steps[shownStep.order - 2];
                            if (prev) setEditingStepId(prev.id);
                          }
                        : null
                    }
                    structuredNode={
                      shownStep.modes.includes('structured') &&
                      STRUCTURED_WIDGETS[shownStep.id]
                        ? STRUCTURED_WIDGETS[shownStep.id]({
                            busy,
                            onSubmit: (structured) =>
                              runTurn({ stepId: shownStep.id, structured }),
                            onChange:
                              shownStep.id === 'brand'
                                ? setBrandDraft
                                : undefined,
                          })
                        : null
                    }
                    skippable={shownStep.skippable}
                    onSkip={() => runSkip(shownStep.id)}
                  />
                </Box>
              ) : (
                <ReviewConfirm
                  steps={steps}
                  data={state ? state.data : null}
                  busy={busy}
                  readyToConfirm={Boolean(progress.readyToConfirm)}
                  teamForkIntro={state ? state.teamForkIntro : ''}
                  onEdit={startEdit}
                  onConfirm={runConfirm}
                  onStartOver={runReset}
                />
              )}
            </CardContent>
          </Card>

          {/* Right: live preview on Look & feel, else the self-building brief */}
          {isLookAndFeel ? (
            <SitePreview
              colors={previewColors}
              tone={previewTone}
              logoUrl={brandDraft.logoUrl}
              businessName={
                (data.basics &&
                  (data.basics.displayName || data.basics.legalName)) ||
                'Your business'
              }
              industry={(data.basics && data.basics.industry) || ''}
              services={(data.services || []).map((s) => ({ name: s.name }))}
              areas={(data.serviceArea && data.serviceArea.areas) || []}
            />
          ) : (
            <Box
              sx={{ width: { xs: '100%', md: 'auto' }, flexShrink: { md: 0 } }}
            >
              {/* mobile: tappable summary row that expands the brief */}
              <Box
                onClick={() => setBriefOpen((o) => !o)}
                sx={{
                  display: { xs: 'flex', md: 'none' },
                  alignItems: 'center',
                  gap: 1,
                  p: 1.25,
                  borderRadius: '12px',
                  background: (theme) => theme.custom.tokens.card,
                  border: (theme) =>
                    `1px solid ${theme.custom.tokens.cardBorder}`,
                  boxShadow: (theme) => theme.custom.tokens.cardShadowSoft,
                  cursor: 'pointer',
                }}
              >
                <CompletionRing
                  percent={progress.percent || 0}
                  size={32}
                  stroke={4}
                />
                <Typography sx={{ fontWeight: 800, fontSize: 14 }}>
                  Your brief
                </Typography>
                <Typography sx={{ color: 'text.secondary', fontSize: 12.5 }}>
                  {doneCount} of {steps.length || 10}
                </Typography>
                <Box sx={{ flex: 1 }} />
                <ExpandMoreIcon
                  sx={{
                    color: 'text.secondary',
                    transition: 'transform .2s',
                    transform: briefOpen ? 'rotate(180deg)' : 'none',
                  }}
                />
              </Box>
              {/* the panel: collapsible on mobile, always shown on desktop */}
              <Box
                sx={{
                  display: { xs: briefOpen ? 'block' : 'none', md: 'block' },
                  mt: { xs: 1, md: 0 },
                }}
              >
                <BriefPanel
                  steps={steps}
                  data={state ? state.data : null}
                  activeId={shownStep ? shownStep.id : null}
                  percent={progress.percent || 0}
                  completed={completed}
                  busy={busy}
                  onEdit={startEdit}
                  customFlags={state ? state.customFlags : []}
                />
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
